import { ColorTypes, dom, events } from '@omegagrid/core';
import { FormContainer } from "@omegagrid/form";
import { msg } from "@omegagrid/localize";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { AppComponent, Application, ParsedComponentId, GridEditor, SyncGridEditorAdapter, EditorContainer } from '@reporting/rp-app-host';
import { StatementDefinition } from "@reporting/rp-types/definition";
import { FinPeriodClosingContainer } from './components';
import { FinDropdownAdapter } from './adapters';
import { FinAccountsGridAdapter, FinAccountsGridEditorAdapter, ScenarioGridEditorAdapter, FinTreeGridEditorAdapter,
	FinReportFormAdapter, FinRowsGridEditorAdapter, FinAggregateGridEditorAdapter, FinTreeCopyFormAdapter,
	FinAccountsDeleteFormAdapter, FinReportCopyFormAdapter, FinExportFormAdapter, FinExportType, FinSyncEditorAdapter, FiscalYearGridEditorAdapter,
	FinAggregatedRowsGridEditorAdapter, FinReportsGridEditorAdapter, ReportStatsGridAdapter, ReportRecoveryGridAdapter
} from './adapters/finance';
import { DefinitionApplication } from '.';

export const createFinReportsGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinReportsGridEditorAdapter();
	return component;
}

export const createFinAggregateGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const idRow = parseInt(componentId.ids[1]);
	if (!idRow) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinAggregateGridEditorAdapter(idRow);
	return component;
}

export const createFinAggregatedRowsGridEditor = async (app: DefinitionApplication, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinAggregatedRowsGridEditorAdapter(statement);
	return component;
}

export const createFinTreeGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const year = parseInt(componentId.ids[2]) || new Date().getFullYear(); // set actual year if empty
	const idRow = parseInt(componentId.ids[3]) || null;
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinTreeGridEditorAdapter(statement, year, idRow);
	return component;
}

export const createFinPeriodClosingContainer = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<FinPeriodClosingContainer>('rp-definition-finperiodclosing');
	return component as unknown as AppComponent;
}

const finAccountsGridId = (idStatement: number, year: number) => idStatement
	? (year ? `definition-finAccountsGridEditor-${idStatement}-${year}` : `definition-finAccountsGrid-${idStatement}`)
	: 'definition-finAccountsGrid';

const finAccountsContainerId = (idStatement: number, year: number) =>
	`definition-finAccounts${idStatement ? `-${idStatement}${year ? `-${year}` : ''}` : ''}`;

/**
 * The accounts of the statement and year picked in the toolbar (an editor), or the overview of the accounts (of one
 * statement) until the year is picked. The opening selection comes from the component id and the container is renamed
 * after the selection made, so a saved layout opens it again and the deletion form finds it.
 */
export const createFinAccountsContainer = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]) || null;
	const current = {
		statement: idStatement ? await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`) : null,
		year: parseInt(componentId.ids[2]) || null,
	};
	const createItems = (): ToolbarItem[] => [{
		type: 'dropdown',
		id: 'statement',
		width: 200,
		caption: msg('Report'),
		adapter: new FinDropdownAdapter('statements'),
		value: current.statement ? [[current.statement.id_statement, 0, 1, {v: current.statement.statement}]] : null,
	}, {
		type: 'dropdown',
		id: 'year',
		width: 120,
		caption: msg('Year'),
		adapter: new FinDropdownAdapter('years'),
		value: current.year ? [[current.year, 0, 1]] : null,
	}, {
		type: 'button',
		id: 'clear',
		icon: 'xmark',
		color: ColorTypes.Red,
		title: msg('Clear'),
	}, {
		type: 'button',
		id: 'refresh',
		icon: 'arrows-rotate',
		color: ColorTypes.Gray,
		title: msg('Refresh'),
	}];
	// a dropdown is among the toolbar values once it was touched (null = cleared), until then the opening selection holds
	const selectedId = (values: Record<string, unknown>, key: 'statement'|'year') => key in values
		? parseInt(values[key] as string) || null
		: (key == 'statement' ? current.statement?.id_statement ?? null : current.year);

	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: createItems(),
		cacheComponents: true, // the grids of the statements visited stay as they were left, unsaved edits included
		innerComponentId: finAccountsGridId(current.statement?.id_statement, current.year),
		createComponent: id => rp.host.createComponent(id),
		updateComponentId: values => finAccountsGridId(selectedId(values, 'statement'), selectedId(values, 'year')),
	});

	container.addEventListener('change', async (e: events.ChangeEvent<Record<string, unknown>>) => {
		const idStatement = selectedId(e.value, 'statement');
		current.year = selectedId(e.value, 'year');
		if (idStatement !== (current.statement?.id_statement ?? null)) {
			current.statement = idStatement ? await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`) : null;
		}
		rp.host.updateComponent(container.componentId, {
			id: finAccountsContainerId(current.statement?.id_statement, current.year),
			title: `${msg('Accounts')}${current.statement ? `: ${current.statement.statement}` : ''}`,
		});
	});
	// the dropdown lists are reloaded with the selection kept; clearing goes back to the overview of all accounts
	const resetToolbar = () => {
		container.items = createItems();
		container.setValues({});
	};
	container.addEventListener('toolbar.action.clear', () => {
		current.statement = current.year = null;
		resetToolbar();
	});
	container.addEventListener('toolbar.action.refresh', resetToolbar);
	return container;
}

export const createFinAccountsGrid = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]) || null;
	const statement = idStatement ? await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`) : null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinAccountsGridAdapter(statement);
	return component;
}

export const createFinAccountsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${parseInt(componentId.ids[1])}`);
	if (!statement) return null;
	const year = parseInt(componentId.ids[2]);
	if (!statement || !year) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinAccountsGridEditorAdapter(statement, year);
	return component;
}

export const createFinReportForm = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const statement = idStatement ? await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`) : null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FinReportFormAdapter(statement);
	component.addEventListener('form.save', () => {
		if (app.host.components.has('definition-finReports')) {
			(app.host.components.get('definition-finReports') as GridEditor).refresh();
		}
	});
	return component as unknown as AppComponent;
}

export const createFinRowsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FinRowsGridEditorAdapter(statement);
	return component;
}

export const createFinTreeCopyForm = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const year = parseInt(componentId.ids[2]) || null;
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FinTreeCopyFormAdapter(statement, year);
	return component as unknown as AppComponent;
}

export const createFinReportCopyForm = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FinReportCopyFormAdapter(statement);
	component.addEventListener('form.save', () => {
		if (app.host.components.has('definition-finReports')) {
			(app.host.components.get('definition-finReports') as GridEditor).refresh();
		}
	});
	return component as unknown as AppComponent;
}

export const createFinExportForm = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FinExportFormAdapter(statement);
	return component as unknown as AppComponent;
}

export const createFinAccountsDeleteForm = async (app: Application, componentId: ParsedComponentId) => {
	const idStatement = parseInt(componentId.ids[1]);
	const year = parseInt(componentId.ids[2]) || null;
	const statement = await rp.ms('definition').get<StatementDefinition>(`statements/${idStatement}`);
	if (!statement) return null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FinAccountsDeleteFormAdapter(statement, year);
	component.addEventListener('form.save', async () => {
		const id = finAccountsContainerId(statement.id_statement, year);
		if (app.host.components.has(id)) {
			const container = await app.host.components.get(id) as EditorContainer;
			(container.component as GridEditor)?.refresh();
		}
	});
	return component as unknown as AppComponent;
}

export const createScenarioGrid = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ScenarioGridEditorAdapter();
	return component;
}

export const createSyncEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SyncGridEditorAdapter(new FinSyncEditorAdapter());
	return component;
}

export const createFiscalYearGrid = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new FiscalYearGridEditorAdapter();
	return component;
}

export const createReportStatsGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ReportStatsGridAdapter();
	return component;
}

export const createReportRecoveryGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ReportRecoveryGridAdapter();
	return component;
}
